// Mock catalog for the Feature Library modal scaffold.
//
// This array is the de-facto data contract for issue #601393, which will
// enrich `Sidebars::MenuItem` with `description`, `category`, `tier`,
// `enhanced_tiers`, `badges`, `panels`, and `recommended` fields and replace
// this fixture with server-driven data.
//
// Every entry resolves to a real, pinnable nav item: each `item_id` matches an
// id in its declared panel's `super_sidebar_menu_items` tree, so pinning it
// navigates to the real resource. Ids that did not resolve (invented EE ids,
// or ids that only exist under a different name in a given panel) were removed
// rather than shipped broken. `panels` lists only the panels where the id
// actually maps — e.g. `project_issue_list` is project-only because the group
// panel exposes `group_issue_list` instead.

/* eslint-disable @gitlab/require-i18n-strings -- Mock fixture data, replaced by server-driven catalog in https://gitlab.com/gitlab-org/gitlab/-/issues/601393 */

import { TIERS } from './constants';

export const MOCK_CATALOG = Object.freeze([
  {
    item_id: 'repository',
    title: 'Repository',
    description: 'Browse and manage your code',
    icon: 'code',
    category: 'code',
    tier: TIERS.FREE,
    enhanced_tiers: [],
    badges: [],
    panels: ['project'],
    recommended: false,
  },
  {
    item_id: 'project_issue_list',
    title: 'Work items',
    description: 'Track tasks and issues',
    icon: 'issues',
    category: 'plan',
    tier: TIERS.FREE,
    enhanced_tiers: [],
    badges: [],
    panels: ['project'],
    recommended: false,
  },
  {
    item_id: 'project_merge_request_list',
    title: 'Merge requests',
    description: 'Review code changes',
    icon: 'merge-request',
    category: 'code',
    tier: TIERS.FREE,
    enhanced_tiers: [],
    badges: [],
    panels: ['project'],
    recommended: false,
  },
  {
    item_id: 'branches',
    title: 'Branches',
    description: 'Manage project branches and branch protection',
    icon: 'branch',
    category: 'code',
    tier: TIERS.FREE,
    enhanced_tiers: [],
    badges: [],
    panels: ['project'],
    recommended: false,
  },
  {
    item_id: 'pipelines',
    title: 'Pipelines',
    description: 'Automate your workflows',
    icon: 'pipeline',
    category: 'build',
    tier: TIERS.FREE,
    enhanced_tiers: [],
    badges: [],
    panels: ['project'],
    recommended: false,
  },
  {
    item_id: 'vulnerability_report', // EE id
    title: 'Vulnerability Report',
    description: 'Monitor and address security risks in your project',
    icon: 'shield',
    category: 'secure',
    tier: TIERS.FREE,
    enhanced_tiers: [TIERS.PREMIUM],
    panels: ['project'],
    badges: [],
    recommended: true,
  },
  {
    item_id: 'cycle_analytics',
    title: 'Value Stream Analytics',
    description: 'Measure development cycle time and efficiency',
    icon: 'comparison',
    category: 'analyze',
    tier: TIERS.PREMIUM,
    enhanced_tiers: [],
    panels: ['project'],
    badges: [],
    recommended: true,
  },
  {
    item_id: 'activity',
    title: 'Activity',
    description: 'Track what your team is working on and stay informed of changes',
    icon: 'history',
    category: 'plan',
    tier: TIERS.FREE,
    enhanced_tiers: [],
    badges: [],
    panels: ['project', 'group'],
    recommended: false,
  },
  {
    item_id: 'audit_events', // EE id
    title: 'Audit Events',
    description: 'See a complete record of all project changes and access',
    icon: 'log',
    category: 'manage',
    tier: TIERS.PREMIUM,
    enhanced_tiers: [],
    badges: [],
    panels: ['project'],
    recommended: false,
  },
  {
    item_id: 'boards',
    title: 'Boards',
    description: 'Visualize and prioritize work with kanban-style boards',
    icon: 'list-numbered',
    category: 'plan',
    tier: TIERS.FREE,
    enhanced_tiers: [],
    badges: [],
    panels: ['project'],
    recommended: false,
  },
  {
    item_id: 'ci_cd_analytics',
    title: 'CI/CD Analytics',
    description: 'Analyze pipeline performance and trends',
    icon: 'chart',
    category: 'analyze',
    tier: TIERS.FREE,
    enhanced_tiers: [],
    badges: [],
    panels: ['project'],
    recommended: false,
  },
  {
    item_id: 'commits',
    title: 'Commits',
    description: 'View commit history and changes',
    icon: 'commit',
    category: 'code',
    tier: TIERS.FREE,
    enhanced_tiers: [],
    badges: [],
    panels: ['project'],
    recommended: false,
  },
  {
    item_id: 'compliance', // EE id
    title: 'Compliance Center',
    description: 'Track regulatory requirements and compliance progress',
    icon: 'scale',
    category: 'secure',
    tier: TIERS.ULTIMATE,
    enhanced_tiers: [],
    badges: [],
    panels: ['project'],
    recommended: false,
  },
  {
    item_id: 'dependency_list', // EE id
    title: 'Dependency List',
    description: 'Manage project dependencies',
    icon: 'review-list',
    category: 'secure',
    tier: TIERS.PREMIUM,
    enhanced_tiers: [],
    badges: [],
    panels: ['project'],
    recommended: false,
  },
  {
    item_id: 'environments',
    title: 'Environments',
    description: 'Manage deployment environments',
    icon: 'environment',
    category: 'deploy',
    tier: TIERS.FREE,
    enhanced_tiers: [],
    badges: [],
    panels: ['project'],
    recommended: false,
  },
  {
    item_id: 'members',
    title: 'Members',
    description: 'Manage project members and access',
    icon: 'users',
    category: 'manage',
    tier: TIERS.FREE,
    enhanced_tiers: [],
    badges: [],
    panels: ['project', 'group'],
    recommended: false,
  },
]);
